// todayGauntletPanel.ts - the daily ritual surface (Tactical OLED redesign). A focused, full-bleed
// "today's board" screen: a dated header, the shared featured topic, a 7-day streak strip, and ONE
// primary CTA that routes PLAY through MenuScene's daily launch callback. Reads the live streak from
// stats; everything else is the shared-board framing. No Core/network/data touch.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { ctaButton, labelText, chip } from './ui/kit';
import { icon, type IconName } from './icons';
import { scaledFont, motionAllowed } from './settings';
import { audio } from './audio';
import { getStats, liveDailyStreak } from './stats';
import { readLastGhost } from './lastGhost';
import { drawSpectralGhost, SPECTRAL_BODY } from './mascot';
import { getTopScores } from '../net/leaderboard';
import { getDaily } from '../net/boardClient';
import { getDailyGhosts } from '../net/ghostClient';
import { mutatorForDay } from './dailyMutator';
import { dailyDifficulty } from '../core';
import { GHOST_BALANCE_VERSION, parseBootParams } from './registryKeys';
import { decodeTrack } from './ghostTrack';
import { readLadder, pickRungs, type LadderRace } from './ghostLadder';
import type { GhostRecord } from '../net/types';

export interface TodayGauntletOpts {
  /** Launch the Daily. `raceGhost` requests the local self-race; `ladder` requests a specific ghost-ladder
   *  duel (record + rung — the run forces the rival on). GameScene fails open to a plain daily on a pin miss. */
  onPlay: (opts?: { raceGhost?: boolean; ladder?: LadderRace }) => void;
}

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
type DiscState = 'done' | 'freeze' | 'today' | 'future';

export function openTodayGauntlet(scene: Phaser.Scene, opts: TodayGauntletOpts): ReturnType<typeof modal> {
  const stats = getStats();
  const streak = liveDailyStreak(stats);
  const desktop = scene.scale.width >= 900;

  // Full-screen daily surface: empty title (the top-rail still brands it), scan rail by default.
  const m = modal(scene, { title: '', fullBleed: true, widthPx: desktop ? 940 : undefined });
  const { card, cardW, bodyTop, footerY, close } = m;

  const fPadX = desktop ? 22 : 18;
  const panelW = cardW - (desktop ? 52 : 44);
  const innerLeft = -panelW / 2 + fPadX;
  const innerRight = panelW / 2 - fPadX;

  // ── Header: dated eyebrow + two-tone title ──
  const now = new Date();
  const eyebrow = `${WEEKDAYS[now.getDay()]} · ${MONTHS[now.getMonth()]} ${now.getDate()}`;
  let y = bodyTop + 10;
  card.add(labelText(scene, 0, y, eyebrow, { size: 13, color: PALETTE.scan, origin: 0.5 }));
  y += 34;
  twoToneTitle(scene, card, y, "TODAY'S ", PALETTE.text, 'GAUNTLET', PALETTE.gold, scaledFont(33, 20), cardW - 40);
  y += 32;

  // ── Featured topic card ──
  // Today's ACTUAL profile: the day's mutator applied to the daily base — the meta row and twist
  // line derive from these params, never hand-written copy (they can't drift from Core).
  const twist = mutatorForDay();
  const dparams = dailyDifficulty(undefined, undefined, twist.id);
  const fh = desktop ? 148 : 158;
  const fTop = y;
  cardBox(scene, card, fTop, panelW, fh);
  card.add(
    chip(scene, innerLeft, fTop + 26, 'EVERYONE PLAYS THE SAME BOARD', {
      textColor: PALETTE.scan,
      borderColor: PALETTE.scan,
      fontPx: 10,
      padX: 11,
      height: 24,
      originX: 0,
    }),
  );
  card.add(icon(scene, 'shield', innerRight - 11, fTop + 26, 22, PALETTE.correct));
  // Topic is fetched LIVE (was a fabricated static string — trust rule: never fake the product).
  // The daily client falls back to a deterministic seed board offline, so this always resolves.
  const topicText = scene.add
    .text(innerLeft, fTop + 70, 'Cooking today’s topic…', {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(27, 18)}px`,
      color: hex(PALETTE.text),
      fontStyle: 'bold',
    })
    .setOrigin(0, 0.5);
  card.add(topicText);
  // Fetch today's board ONCE and reuse: the topic label AND the ghost-ladder filter both pin on its
  // topicNormalized (a ghost only races the board it was recorded on).
  const dailyP = getDaily();
  dailyP
    .then((res) => {
      if (topicText.active) {
        const raw = res.board.topicRaw || res.board.topicNormalized || 'Today’s topic';
        topicText.setText(raw.length > 24 ? `${raw.slice(0, 23)}…` : raw);
      }
    })
    .catch(() => {
      if (topicText.active) topicText.setText('Today’s topic');
    });
  // Twist line — the day's identity, straight from the mutator table.
  card.add(
    scene.add
      .text(innerLeft, fTop + 98, `TWIST · ${twist.name} — ${twist.blurb}`, {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.armed),
        fontStyle: '600',
        wordWrap: { width: panelW - fPadX * 2, useAdvancedWrap: true },
      })
      .setOrigin(0, 0.5),
  );
  // meta row: gauntlet fence share (armed) · hazards (hazard) · soft timer (dim) — param-derived.
  const hazardCount = dparams.hazardTypes.length;
  let mx = innerLeft;
  // S3 item 12: fenceRatio is the SHARE of correct answers fenced behind decoys (0.3 = 30%), NOT a
  // score multiplier — "×0.3" read as a 0.3× penalty. Honest copy: "Fenced 30%".
  mx = metaItem(scene, card, mx, fTop + 126, 'bolt', PALETTE.armed, `Fenced ${Math.round(dparams.fenceRatio * 100)}%`);
  mx = metaSep(scene, card, mx, fTop + 126);
  mx = metaItem(scene, card, mx, fTop + 126, 'sword', PALETTE.hazard, `${hazardCount} hazard${hazardCount === 1 ? '' : 's'}`);
  mx = metaSep(scene, card, mx, fTop + 126);
  metaItem(scene, card, mx, fTop + 126, 'clock', PALETTE.textDim, `${Math.round(dparams.softTimerMs / 1000)}s`);

  // ── Streak card ──
  const sh = desktop ? 134 : 144;
  const sTop = fTop + fh + 16;
  cardBox(scene, card, sTop, panelW, sh);
  card.add(icon(scene, 'flame', innerLeft + 9, sTop + 26, 18, PALETTE.armed));
  card.add(labelText(scene, innerLeft + 24, sTop + 26, streak > 0 ? `${streak}-DAY STREAK` : 'START YOUR STREAK', { size: 13, color: PALETTE.armed }));
  // right-aligned "Freeze ready" with a snowflake
  const freezeTxt = scene.add
    .text(innerRight, sTop + 26, 'Freeze ready', { fontFamily: FONT.display, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.scan), fontStyle: '600' })
    .setOrigin(1, 0.5);
  card.add(freezeTxt);
  card.add(icon(scene, 'snowflake', innerRight - freezeTxt.width - 16, sTop + 26, 16, PALETTE.scan));

  // 7-day disc strip
  const dow = (now.getDay() + 6) % 7; // 0 = Mon .. 6 = Sun
  const freezeIdx = dow - 2; // illustrative freeze token slot
  const slot = (panelW - fPadX * 2) / 7;
  const discR = Math.min(19, slot * 0.3);
  const rowY = sTop + 84;
  const startX = innerLeft + slot / 2;
  for (let i = 0; i < 7; i++) {
    let state: DiscState;
    if (i === dow) state = 'today';
    else if (i > dow) state = 'future';
    else if (streak <= 0) state = 'future';
    else if (i === freezeIdx) state = 'freeze';
    else state = 'done';
    const dx = startX + i * slot;
    dayDisc(scene, card, dx, rowY, discR, state);
    const labelColor = state === 'today' ? PALETTE.gold : state === 'freeze' ? PALETTE.scan : state === 'done' ? PALETTE.textDim : PALETTE.mute;
    card.add(
      scene.add
        .text(dx, rowY + discR + 15, DAY_LETTERS[i], { fontFamily: FONT.display, fontSize: `${scaledFont(11, 10)}px`, color: hex(labelColor), fontStyle: '600' })
        .setOrigin(0.5),
    );
  }

  // ── GHOST DUELS Slice A: the "Race your last run" SELF-RACE CARD (#15) — promoted from a thin text chip
  // to a real card between the streak strip and the footer: an original ghost silhouette, the score "to
  // beat", and a RACE pill (the whole card is the tap target). It launches the SAME daily with the
  // self-race flag; a pin miss just plays a normal daily (fail-open — never blocked). With no saved ghost
  // yet it shows a faded silhouette + an honest empty state instead of vanishing (the audit's dead gap).
  const savedGhost = readLastGhost();
  const selfRaceTop = sTop + sh + 16;
  const selfRaceH = desktop ? 70 : 66;
  const selfCy = selfRaceTop + selfRaceH / 2;
  cardBox(scene, card, selfRaceTop, panelW, selfRaceH);
  {
    const gsize = 30;
    const badgeX = innerLeft + gsize * 0.7;
    const badge = scene.add.graphics();
    badge.fillStyle(SPECTRAL_BODY, savedGhost ? 0.18 : 0.08);
    badge.fillRoundedRect(badgeX - gsize / 2, selfCy - gsize / 2, gsize, gsize, 9);
    badge.lineStyle(1.5, savedGhost ? SPECTRAL_BODY : PALETTE.lineStrong, savedGhost ? 0.95 : 0.75);
    badge.strokeRoundedRect(badgeX - gsize / 2, selfCy - gsize / 2, gsize, gsize, 9);
    card.add(badge);
    card.add(icon(scene, savedGhost ? 'target' : 'sparkle', badgeX, selfCy, 18, savedGhost ? SPECTRAL_BODY : PALETTE.mute));
    const textX = innerLeft + gsize * 1.4 + 10;
    if (savedGhost) {
      const scoreStr = savedGhost.score.toLocaleString('en-US');
      card.add(labelText(scene, textX, selfCy - 11, 'RACE YOUR LAST RUN', { size: 12, color: PALETTE.text, origin: 0 }));
      card.add(
        scene.add
          .text(textX, selfCy + 12, `to beat · ${scoreStr}`, {
            fontFamily: FONT.display,
            fontSize: `${scaledFont(13, 11)}px`,
            color: hex(PALETTE.scan),
            fontStyle: '600',
          })
          .setOrigin(0, 0.5),
      );
      // RACE pill (visual affordance; the full card below is the hit target for a big, thumb-friendly tap).
      const pillW = 74;
      const pillH = 30;
      const pillCx = innerRight - pillW / 2;
      const pg = scene.add.graphics();
      pg.fillStyle(PALETTE.scan, 1);
      pg.fillRoundedRect(pillCx - pillW / 2, selfCy - pillH / 2, pillW, pillH, pillH / 2);
      card.add(pg);
      card.add(
        scene.add
          .text(pillCx, selfCy, 'RACE', { fontFamily: FONT.display, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.ink), fontStyle: 'bold' })
          .setOrigin(0.5),
      );
      const hit = scene.add.rectangle(0, selfCy, panelW, selfRaceH, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => {
        audio().playSfx('uiConfirm');
        close();
        opts.onPlay({ raceGhost: true });
      });
      card.add(hit);
    } else {
      card.add(labelText(scene, textX, selfCy - 11, 'NO GHOST YET', { size: 12, color: PALETTE.textDim, origin: 0 }));
      card.add(
        scene.add
          .text(textX, selfCy + 12, 'Finish today to race yourself next time.', {
            fontFamily: FONT.family,
            fontSize: `${scaledFont(12, 10)}px`,
            color: hex(PALETTE.mute),
          })
          .setOrigin(0, 0.5),
      );
    }
  }

  // ── Footer: primary CTA (green + gold ring) + leaderboard caption ──
  // S3b gap 3 — CONTENT-HUGGING. The CTA + caption FLOW just below the ghost-ladder section (whose rung
  // count is async) and the card is resized to hug them, killing the dead band the audit found beneath a
  // short ladder. `footerCtaY` is the live CTA anchor; `hugToday` places it a fixed gap under the content
  // and shrinks the card bottom to match. Defined BEFORE the ladder so its onRendered hook can re-hug.
  let footerCtaY = footerY - 16;
  let footerParts: { t: string; c: number; bold?: boolean }[] = [
    { t: "Fresh board — set today's score to beat.", c: PALETTE.textDim },
  ];
  const cta = ctaButton(
    scene,
    0,
    footerCtaY,
    'Play — Keep the Streak',
    () => {
      close();
      opts.onPlay();
    },
    { width: panelW, height: 60, icon: 'play', glow: PALETTE.gold, fontPx: 18 },
  );
  card.add(cta);
  // REAL daily standings only (a hard-coded "quizlord · you're #4" used to live here — fabricated
  // social proof violates the trust pillar and store policy). Honest empty state until data lands.
  const footerRun = scene.add.container(0, 0);
  card.add(footerRun);
  const renderFooter = (parts: { t: string; c: number; bold?: boolean }[]): void => {
    footerParts = parts;
    footerRun.removeAll(true);
    centeredRun(scene, footerRun, footerCtaY - 56, scaledFont(12, 10), parts);
  };
  const hugToday = (contentBottomLocal: number): void => {
    footerCtaY = contentBottomLocal + 88; // support line + clear gap + CTA half-height
    cta.setY(footerCtaY);
    m.relayout(footerCtaY + 30 + 24 - bodyTop); // CTA bottom + cushion
    renderFooter(footerParts);
  };

  // ── GHOST DUELS Slice C: the BYT3-gatekeeper daily ghost LADDER — a compact section between the self-race
  // row and the footer. Gate row (local, synchronous) + up to 3 rung rows (the day's ghosts, fetched + pinned
  // async). Fully fail-closed: a cold/empty store shows the gate + an honest race-ghost line; the daily
  // is never blocked. Names capped 12 (privacy). onRendered fires with the section's real bottom (sync loading
  // estimate + async final) so the footer + card hug to whatever rung count actually lands.
  // The self-race CARD is ALWAYS present now (filled or empty state), so the ladder always seats below it.
  const ladderTop = selfRaceTop + selfRaceH + 14;
  const ladderInfo = buildGhostLadder(scene, card, {
    top: ladderTop,
    panelW,
    innerLeft,
    innerRight,
    close,
    onRace: (race) => opts.onPlay({ ladder: race }),
    twistId: twist.id,
    dailyP,
    onRendered: hugToday,
  });
  hugToday(ladderInfo.loadingBottomY); // hug to the loading/gate state immediately; re-hug on data

  renderFooter([{ t: "Fresh board — set today's score to beat.", c: PALETTE.textDim }]);
  void getTopScores({ scope: 'daily', limit: 10 }).then(({ entries }) => {
    if (!footerRun.active || entries.length === 0) return;
    const top = entries[0]!;
    const name = top.name.length > 14 ? `${top.name.slice(0, 13)}…` : top.name;
    const youIdx = entries.findIndex((e) => e.isYou);
    renderFooter([
      { t: "Today's top: ", c: PALETTE.textDim },
      { t: `${name} ${top.score.toLocaleString('en-US')}`, c: PALETTE.gold, bold: true },
      { t: youIdx >= 0 ? ` · you're #${youIdx + 1}` : ' · beat it to rank', c: PALETTE.textDim },
    ]);
  }).catch(() => { /* offline/local-only — the honest fresh-board line stands */ });

  return m;
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** A subtle inset card surface (surface2 fill + visible hairline) drawn from its top edge. */
function cardBox(scene: Phaser.Scene, card: Phaser.GameObjects.Container, top: number, w: number, h: number): void {
  const g = scene.add.graphics();
  g.fillStyle(PALETTE.surface2, 1);
  g.fillRoundedRect(-w / 2, top, w, h, 14);
  g.lineStyle(1.25, PALETTE.lineStrong, 1);
  g.strokeRoundedRect(-w / 2, top, w, h, 14);
  card.add(g);
}

/** Two-tone centered display title (e.g. TODAY'S + GAUNTLET-in-gold), clamped to maxW. */
function twoToneTitle(
  scene: Phaser.Scene,
  card: Phaser.GameObjects.Container,
  y: number,
  left: string,
  leftColor: number,
  right: string,
  rightColor: number,
  fontPx: number,
  maxW: number,
): void {
  const mk = (str: string, color: number): Phaser.GameObjects.Text =>
    scene.add.text(0, 0, str.toUpperCase(), { fontFamily: FONT.display, fontSize: `${fontPx}px`, color: hex(color), fontStyle: 'bold' }).setOrigin(0, 0.5);
  const a = mk(left, leftColor);
  const b = mk(right, rightColor);
  const total = a.width + b.width;
  const tc = scene.add.container(0, y);
  a.setX(-total / 2);
  b.setX(-total / 2 + a.width);
  tc.add([a, b]);
  if (total > maxW) tc.setScale(maxW / total);
  card.add(tc);
}

/** Icon + label meta token, left-anchored at x; returns the x just past the label. */
function metaItem(scene: Phaser.Scene, card: Phaser.GameObjects.Container, x: number, y: number, name: IconName, iconColor: number, label: string): number {
  const isz = 15;
  card.add(icon(scene, name, x + isz / 2, y, isz, iconColor));
  const t = scene.add
    .text(x + isz + 6, y, label, { fontFamily: FONT.family, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.textDim), fontStyle: '600' })
    .setOrigin(0, 0.5);
  card.add(t);
  return x + isz + 6 + t.width;
}

/** A dim middot separator between meta tokens; returns the x just past it. */
function metaSep(scene: Phaser.Scene, card: Phaser.GameObjects.Container, x: number, y: number): number {
  const t = scene.add.text(x + 9, y, '·', { fontFamily: FONT.family, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.mute), fontStyle: '600' }).setOrigin(0, 0.5);
  card.add(t);
  return x + 9 + t.width + 9;
}

/** One streak day disc: done=amber check, freeze=scan snowflake, today=gold star, future=dashed. */
function dayDisc(scene: Phaser.Scene, card: Phaser.GameObjects.Container, cx: number, cy: number, r: number, state: DiscState): void {
  const g = scene.add.graphics();
  if (state === 'today') {
    g.fillStyle(PALETTE.gold, 0.22);
    g.fillCircle(cx, cy, r + 4);
    g.fillStyle(PALETTE.gold, 1);
    g.fillCircle(cx, cy, r);
    card.add(g);
    card.add(icon(scene, 'star', cx, cy, r * 1.15, PALETTE.ink));
    return;
  }
  if (state === 'future') {
    g.lineStyle(1.5, PALETTE.mute, 0.9);
    const dashes = 14;
    for (let i = 0; i < dashes; i++) {
      const a0 = (i / dashes) * Math.PI * 2;
      g.beginPath();
      g.arc(cx, cy, r, a0, a0 + (Math.PI * 2 / dashes) * 0.58, false);
      g.strokePath();
    }
    card.add(g);
    return;
  }
  const accent = state === 'freeze' ? PALETTE.scan : PALETTE.armed;
  g.fillStyle(accent, 0.15);
  g.fillCircle(cx, cy, r);
  g.lineStyle(1.75, accent, 1);
  g.strokeCircle(cx, cy, r);
  card.add(g);
  card.add(icon(scene, state === 'freeze' ? 'snowflake' : 'check', cx, cy, r * 1.2, accent));
}

/** A horizontally-centered multi-color text run at local y. */
function centeredRun(
  scene: Phaser.Scene,
  card: Phaser.GameObjects.Container,
  y: number,
  fontPx: number,
  parts: { t: string; c: number; bold?: boolean }[],
): void {
  const texts = parts.map((p) =>
    scene.add.text(0, 0, p.t, { fontFamily: FONT.family, fontSize: `${fontPx}px`, color: hex(p.c), fontStyle: p.bold ? 'bold' : 'normal' }).setOrigin(0, 0.5),
  );
  const total = texts.reduce((s, t) => s + t.width, 0);
  let x = -total / 2;
  for (const t of texts) {
    t.setX(x);
    t.setY(y);
    x += t.width;
    card.add(t);
  }
}

// ── GHOST DUELS Slice C: the daily ghost ladder ────────────────────────────────

interface LadderCfg {
  /** card-local y of the section header. */
  top: number;
  panelW: number;
  innerLeft: number;
  innerRight: number;
  /** close the panel before launching a duel. */
  close: () => void;
  /** launch a rung's duel (record + rung). */
  onRace: (race: LadderRace) => void;
  /** today's daily mutator id — a rung's ghost must have been recorded under the same twist. */
  twistId: number;
  /** the shared getDaily() promise — its topicNormalized is the ladder's topic pin. */
  dailyP: ReturnType<typeof getDaily>;
  /** S3b gap 3: fired with the section's card-local bottom Y after the rungs (or empty line) render, so
   *  the panel can hug its footer + card to the real content. */
  onRendered?: (contentBottomY: number) => void;
}

const LADDER_ROW_H = 42;
const LADDER_ROW_STEP = 50;

/** Card-local bottom Y of the ladder section for `n` rendered rungs (0 → the single loading/empty line). */
function ladderBottomY(firstRungY: number, n: number): number {
  return n > 0 ? firstRungY + (n - 1) * LADDER_ROW_STEP + LADDER_ROW_H / 2 : firstRungY + 24;
}

/**
 * Render the GHOST LADDER section: a local (synchronous) gate row + up to three async rung rows. The gate
 * state comes from localStorage (readLadder), so it paints instantly; the rungs stream in after the day's
 * ghosts fetch + pin. Every failure mode (cold store, empty day, topic mismatch, decode fail) degrades to
 * an honest empty line — the section can only ADD, never break the daily.
 */
function buildGhostLadder(scene: Phaser.Scene, card: Phaser.GameObjects.Container, cfg: LadderCfg): { loadingBottomY: number } {
  const { top, panelW, innerLeft, innerRight, close, onRace, twistId, dailyP } = cfg;
  const ladder = readLadder();
  const gateBeaten = ladder.gateBeaten;

  // section header + a right-aligned hint of what the ladder is.
  card.add(labelText(scene, innerLeft, top, 'GHOST LADDER', { size: 12, color: PALETTE.scan }));
  card.add(
    scene.add
      .text(innerRight, top, gateBeaten ? 'Out-run the day’s ghosts' : 'Locked', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(11, 10)}px`,
        // S3 item 13: `dim` (not `mute`) so the "Locked" hint clears WCAG-AA 4.5:1 on the panel.
        color: hex(PALETTE.textDim),
        fontStyle: '600',
      })
      .setOrigin(1, 0.5),
  );

  // gate row (rung 0 = BYT3) — the unlock. It never taps anywhere; it's a status line.
  const gateY = top + 34;
  ladderRow(scene, card, {
    y: gateY,
    w: panelW,
    iconName: gateBeaten ? 'check' : 'sword',
    iconColor: gateBeaten ? PALETTE.correct : PALETTE.armed,
    label: gateBeaten ? 'BYT3’s daily beaten' : 'Beat BYT3’s daily to unlock',
    labelColor: gateBeaten ? PALETTE.correct : PALETTE.text,
    value: 'RUNG 0',
    valueColor: gateBeaten ? PALETTE.correct : PALETTE.textDim,
    border: gateBeaten ? PALETTE.correct : PALETTE.lineStrong,
  });

  // async rung rows live in their own container so the loading line can be swapped for the real rows.
  const firstRungY = gateY + LADDER_ROW_STEP;
  const rowsC = scene.add.container(0, 0);
  card.add(rowsC);
  rowsC.add(
    scene.add
      .text(innerLeft, firstRungY + 6, gateBeaten ? 'Finding today’s challengers…' : 'Unlock to face the day’s ghosts', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.textDim),
      })
      .setOrigin(0, 0.5),
  );

  const todayDate = new Date().toISOString().slice(0, 10);
  const seed = parseBootParams(window.location.search).seed;
  // Self-filter WITHOUT an id: the uploaded ghost's `track` is verbatim the local lastGhost track
  // (leaderboardPanel uploads readLastGhost().track), so a track match = the player's own upload.
  const selfTrack = readLastGhost()?.track;
  Promise.all([dailyP.catch(() => null), getDailyGhosts(todayDate, 10)])
    .then(([res, ghosts]) => {
      if (!rowsC.active) return;
      rowsC.removeAll(true);
      const topic = res?.board.topicNormalized ?? '';
      const pinned: GhostRecord[] = topic
        ? ghosts.filter(
            (g) =>
              g.bv === GHOST_BALANCE_VERSION &&
              g.seed === seed &&
              g.m === twistId &&
              g.topic === topic &&
              g.track !== selfTrack &&
              decodeTrack(g.track) !== null,
          )
        : [];
      pinned.sort((a, b) => b.score - a.score);
      const rungs = pickRungs(pinned);
      renderRungs(scene, rowsC, rungs, { gateBeaten, beatenRungs: ladder.rungsBeaten, firstRungY, panelW, innerLeft, close, onRace });
      cfg.onRendered?.(ladderBottomY(firstRungY, rungs.length));
    })
    .catch(() => {
      if (!rowsC.active) return;
      rowsC.removeAll(true);
      renderRungs(scene, rowsC, [], { gateBeaten, beatenRungs: ladder.rungsBeaten, firstRungY, panelW, innerLeft, close, onRace });
      cfg.onRendered?.(ladderBottomY(firstRungY, 0));
    });

  // Synchronous loading/gate estimate — the panel hugs to this immediately, then re-hugs on data.
  return { loadingBottomY: ladderBottomY(firstRungY, 0) };
}

interface RungRenderCfg {
  gateBeaten: boolean;
  beatenRungs: number[];
  firstRungY: number;
  panelW: number;
  innerLeft: number;
  close: () => void;
  onRace: (race: LadderRace) => void;
}

/** Paint the rung rows (or the honest empty line) into `rowsC`. */
function renderRungs(scene: Phaser.Scene, rowsC: Phaser.GameObjects.Container, rungs: LadderRace[], cfg: RungRenderCfg): void {
  const { gateBeaten, beatenRungs, firstRungY, panelW, innerLeft, close, onRace } = cfg;
  if (rungs.length === 0) {
    rowsC.add(
      scene.add
        .text(innerLeft, firstRungY + 6, gateBeaten ? 'No race ghosts yet — share your ghost!' : 'Race ghosts unlock after BYT3.', {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(12, 11)}px`,
          color: hex(PALETTE.textDim),
        })
        .setOrigin(0, 0.5),
    );
    return;
  }
  rungs.forEach((r, i) => {
    const y = firstRungY + i * LADDER_ROW_STEP;
    const beaten = beatenRungs.includes(r.rung);
    const name = (r.record.name || 'GHOST').slice(0, 12);
    const scoreStr = r.record.score.toLocaleString('en-US');
    // Locked (gate not beaten): a dim teaser of who's waiting, non-interactive. Beaten: green check.
    // Available: gold rank + cyan "you can act" border, tappable to launch the duel.
    const accent = !gateBeaten ? PALETTE.textDim : beaten ? PALETTE.correct : PALETTE.player;
    ladderRow(scene, rowsC, {
      y,
      w: panelW,
      rank: beaten ? undefined : String(r.rung),
      rankColor: gateBeaten ? PALETTE.gold : PALETTE.textDim,
      iconName: beaten ? 'check' : undefined,
      iconColor: PALETTE.correct,
      label: name,
      labelColor: !gateBeaten ? PALETTE.textDim : beaten ? PALETTE.correct : PALETTE.text,
      value: scoreStr,
      valueColor: !gateBeaten ? PALETTE.textDim : beaten ? PALETTE.correct : PALETTE.player,
      border: accent,
      dim: !gateBeaten,
      onClick: gateBeaten
        ? (): void => {
            // #15 STRETCH — a brief spectral "vs GHOST · score" reveal beat frames the duel before the
            // panel closes and the run launches. reduceMotion holds a static card; motion slams it in.
            playGhostReveal(scene, r, () => {
              close();
              onRace(r);
            });
          }
        : undefined,
    });
  });
}

/**
 * #15 STRETCH ghost reveal: a full-scene scrim + the spectral ghost avatar + "VS GHOST · <score>" +
 * "RUNG N", held briefly, then `onDone` closes the panel and launches the duel. Tap skips (motion only).
 * reduceMotion = a static card auto-advancing after a short hold; motion = a slam-in with a soft aura pulse.
 * Exported so evidence capture can drive the real reveal on a live scene (no network ghost state needed).
 */
export function playGhostReveal(scene: Phaser.Scene, race: LadderRace, onDone: () => void): void {
  audio().playSfx('uiTap');
  const W = scene.scale.width;
  const H = scene.scale.height;
  const depth = 21_000;
  const name = (race.record.name || 'GHOST').slice(0, 12).toUpperCase();
  const scoreStr = Math.max(0, Math.floor(race.record.score)).toLocaleString('en-US');
  const motion = motionAllowed();
  let done = false;

  const scrim = scene.add
    .rectangle(W / 2, H / 2, W, H, 0x05060c, 0.88)
    .setScrollFactor(0)
    .setDepth(depth)
    .setInteractive();
  const holder = scene.add.container(W / 2, H * 0.46).setDepth(depth + 1);

  const gsize = Math.min(150, W * 0.32);
  const ghost = scene.add.graphics();
  drawSpectralGhost(ghost, gsize, { alpha: 1 });
  ghost.setY(-40);

  const vs = scene.add
    .text(0, gsize * 0.62, 'VS GHOST', {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(15, 13)}px`,
      color: hex(SPECTRAL_BODY),
      fontStyle: 'bold',
    })
    .setOrigin(0.5);
  const nameScore = scene.add
    .text(0, gsize * 0.62 + 40, `${name} · ${scoreStr}`, {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(30, 22)}px`,
      color: hex(PALETTE.text),
      fontStyle: 'bold',
    })
    .setOrigin(0.5)
    .setShadow(0, 0, hex(SPECTRAL_BODY), 14, false, true);
  const rung = scene.add
    .text(0, gsize * 0.62 + 78, `RUNG ${race.rung} · OUT-RUN IT`, {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(13, 11)}px`,
      color: hex(PALETTE.textDim),
    })
    .setOrigin(0.5);
  holder.add([ghost, vs, nameScore, rung]);

  let bob: Phaser.Tweens.Tween | undefined;
  const finish = (): void => {
    if (done) return;
    done = true;
    scrim.off('pointerdown', finish);
    bob?.stop();
    onDone();
  };
  // Safety: the launch normally transitions scenes (auto-destroying this overlay); if it doesn't, ensure
  // the repeating tween is killed on shutdown so nothing leaks.
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => bob?.stop());

  if (motion) {
    holder.setScale(0.7).setAlpha(0);
    scene.tweens.add({ targets: holder, scale: 1, alpha: 1, duration: 260, ease: 'Back.easeOut' });
    bob = scene.tweens.add({ targets: ghost, y: -52, duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    scene.time.delayedCall(1250, finish);
    scrim.on('pointerdown', finish);
  } else {
    // static hold, no tweens; auto-advance so the flow never stalls under reduced motion.
    scene.time.delayedCall(1000, finish);
  }
}

interface LadderRowOpts {
  y: number;
  w: number;
  rank?: string;
  rankColor?: number;
  iconName?: IconName;
  iconColor?: number;
  label: string;
  labelColor?: number;
  value?: string;
  valueColor?: number;
  fill?: number;
  border?: number;
  dim?: boolean;
  onClick?: () => void;
}

/** One compact ladder row (rank/icon · label · value) added to `parent`; interactive iff `onClick` set. */
function ladderRow(scene: Phaser.Scene, parent: Phaser.GameObjects.Container, o: LadderRowOpts): Phaser.GameObjects.Container {
  const h = LADDER_ROW_H;
  const c = scene.add.container(0, o.y);
  const alpha = o.dim ? 0.55 : 1;
  const g = scene.add.graphics();
  g.fillStyle(o.fill ?? PALETTE.surface, alpha);
  g.fillRoundedRect(-o.w / 2, -h / 2, o.w, h, 10);
  g.lineStyle(1.25, o.border ?? PALETTE.lineStrong, alpha);
  g.strokeRoundedRect(-o.w / 2, -h / 2, o.w, h, 10);
  c.add(g);
  let lx = -o.w / 2 + 15;
  if (o.rank !== undefined) {
    c.add(
      scene.add
        .text(lx, 0, o.rank, { fontFamily: FONT.display, fontSize: `${scaledFont(15, 12)}px`, color: hex(o.rankColor ?? PALETTE.textDim), fontStyle: 'bold' })
        .setOrigin(0, 0.5),
    );
    lx += 22;
  }
  if (o.iconName) {
    c.add(icon(scene, o.iconName, lx + 9, 0, 18, o.iconColor ?? PALETTE.textDim));
    lx += 26;
  }
  c.add(
    scene.add
      .text(lx, 0, o.label, { fontFamily: FONT.family, fontSize: `${scaledFont(14, 12)}px`, color: hex(o.labelColor ?? PALETTE.text), fontStyle: '600' })
      .setOrigin(0, 0.5),
  );
  if (o.value !== undefined) {
    c.add(
      scene.add
        .text(o.w / 2 - 15, 0, o.value, { fontFamily: FONT.display, fontSize: `${scaledFont(14, 12)}px`, color: hex(o.valueColor ?? PALETTE.player), fontStyle: 'bold' })
        .setOrigin(1, 0.5),
    );
  }
  parent.add(c);
  if (o.onClick) {
    const hit = scene.add.rectangle(0, 0, o.w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      audio().playSfx('uiTap');
      o.onClick?.();
    });
    c.add(hit);
  }
  return c;
}
